/**
 * Sample sources — URLs and metadata for each loadable instrument.
 *
 * For MVP we use Tone.js's hosted Salamander Grand Piano. Production should
 * move these to your own CDN (Cloudflare R2 or Vercel static assets) — same
 * file format, just different baseUrl.
 */

import type { Instrument } from "./types.js";

export interface SampleSource {
  baseUrl: string;
  /** Map of pitch name → relative URL of the sample file. */
  urls: Record<string, string>;
  /** Release time in seconds — how long the sample fades out after note off. */
  release: number;
}

export const SAMPLE_SOURCES: Record<Exclude<Instrument, "synth">, SampleSource> = {
  piano: {
    baseUrl: "/samples/piano/",
    urls: {
      // Full chromatic C2–B6 (60 files) served from public/samples/piano/.
      // Naming: sharps use "s" suffix — Cs = C#, Ds = D#, etc.
      C2: "C2.mp3", "C#2": "Cs2.mp3", D2: "D2.mp3",   "D#2": "Ds2.mp3",
      E2: "E2.mp3", F2: "F2.mp3",     "F#2": "Fs2.mp3", G2: "G2.mp3",
      "G#2": "Gs2.mp3", A2: "A2.mp3", "A#2": "As2.mp3", B2: "B2.mp3",

      C3: "C3.mp3", "C#3": "Cs3.mp3", D3: "D3.mp3",   "D#3": "Ds3.mp3",
      E3: "E3.mp3", F3: "F3.mp3",     "F#3": "Fs3.mp3", G3: "G3.mp3",
      "G#3": "Gs3.mp3", A3: "A3.mp3", "A#3": "As3.mp3", B3: "B3.mp3",

      C4: "C4.mp3", "C#4": "Cs4.mp3", D4: "D4.mp3",   "D#4": "Ds4.mp3",
      E4: "E4.mp3", F4: "F4.mp3",     "F#4": "Fs4.mp3", G4: "G4.mp3",
      "G#4": "Gs4.mp3", A4: "A4.mp3", "A#4": "As4.mp3", B4: "B4.mp3",

      C5: "C5.mp3", "C#5": "Cs5.mp3", D5: "D5.mp3",   "D#5": "Ds5.mp3",
      E5: "E5.mp3", F5: "F5.mp3",     "F#5": "Fs5.mp3", G5: "G5.mp3",
      "G#5": "Gs5.mp3", A5: "A5.mp3", "A#5": "As5.mp3", B5: "B5.mp3",

      C6: "C6.mp3", "C#6": "Cs6.mp3", D6: "D6.mp3",   "D#6": "Ds6.mp3",
      E6: "E6.mp3", F6: "F6.mp3",     "F#6": "Fs6.mp3", G6: "G6.mp3",
      "G#6": "Gs6.mp3", A6: "A6.mp3", "A#6": "As6.mp3", B6: "B6.mp3",
    },
    release: 1,
  },
  /**
   * Guitar — chromatic MP3 samples served from public/samples/guitar/.
   * Full fretboard coverage C2–D6 (51 files), no pitch-shifting needed.
   */
  guitar: {
    baseUrl: "/samples/guitar/",
    urls: {
      // Octave 2
      C2: "C2.mp3", "C#2": "Cs2.mp3", D2: "D2.mp3",   "D#2": "Ds2.mp3",
      E2: "E2.mp3", F2: "F2.mp3",     "F#2": "Fs2.mp3", G2: "G2.mp3",
      "G#2": "Gs2.mp3", A2: "A2.mp3", "A#2": "As2.mp3", B2: "B2.mp3",
      // Octave 3
      C3: "C3.mp3", "C#3": "Cs3.mp3", D3: "D3.mp3",   "D#3": "Ds3.mp3",
      E3: "E3.mp3", F3: "F3.mp3",     "F#3": "Fs3.mp3", G3: "G3.mp3",
      "G#3": "Gs3.mp3", A3: "A3.mp3", "A#3": "As3.mp3", B3: "B3.mp3",
      // Octave 4
      C4: "C4.mp3", "C#4": "Cs4.mp3", D4: "D4.mp3",   "D#4": "Ds4.mp3",
      E4: "E4.mp3", F4: "F4.mp3",     "F#4": "Fs4.mp3", G4: "G4.mp3",
      "G#4": "Gs4.mp3", A4: "A4.mp3", "A#4": "As4.mp3", B4: "B4.mp3",
      // Octave 5
      C5: "C5.mp3", "C#5": "Cs5.mp3", D5: "D5.mp3",   "D#5": "Ds5.mp3",
      E5: "E5.mp3", F5: "F5.mp3",     "F#5": "Fs5.mp3", G5: "G5.mp3",
      "G#5": "Gs5.mp3", A5: "A5.mp3", "A#5": "As5.mp3", B5: "B5.mp3",
      // Octave 6
      C6: "C6.mp3", "C#6": "Cs6.mp3", D6: "D6.mp3",
    },
    release: 0.8,
  },
  /**
   * Voice samples — placeholder. Use VocalSet or similar chorale-style "ah"
   * vowel samples. Falls back to synth until wired up.
   */
  voice: {
    baseUrl: "/samples/voice/",
    urls: {
      // TODO: replace with real voice samples
    },
    release: 1.5,
  },
};
